import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppCard } from '@/shared/components/ui/AppCard';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppInput } from '@/shared/components/ui/AppInput';
import { loginSession, getEventsSSOUrl } from '../../api/auth-api';
import { useAuthStore } from '@/shared/stores/auth-store';
import { setSessionToken } from '@/shared/services/session-token';
import { env } from '@/shared/config/env';
import styles from './LoginPage.module.scss';

const EVENTS_COM_ICON =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABEAAAARCAYAAAA7bUf6AAAAy0lEQVR4Aa2TAQ0CMQxFG4KAkzAJkzAJJ+EknAQcgIPhZEEBEiYBHJQ2lFCabrmRe8nPJVvv76/XA3BAxETKpEp6yLOQVlKAHlQQ5YUevD+3DGYcI1mDILH1aUntT3IVm2jSJtkUhEZaa7TqFJoM/b7pxIXXjqRo6jj6CbYRWxGHYI8D7ABf52nWrqQbjOA09gL/gO+R/sDdj50D76TF20wmTeVClGHC77BVVXP2jEa/UkVvKOX0usGgoB75xr0X/O0Tw73KaH+8vXgBpLwaFUCDMNIAAAAASUVORK5CYII=';

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showEventsSSO = env.VITE_EVENTS_SSO_ENABLED;
  const showEmailLogin = !env.VITE_EVENTS_SSO_ENABLED || env.VITE_ENABLE_DEV_TOOLS;

  function handleEventsSSO() {
    window.location.href = getEventsSSOUrl();
  }

  async function handleEmailLogin() {
    setIsLoading(true);
    setError(null);

    try {
      const session = await loginSession(email);
      setSessionToken(session.token);
      setSession({
        user: session.user,
        permissions: session.permissions,
        orgId: session.selectedOrganizationId,
        organizations: session.organizations,
      });

      await navigate({
        to: session.selectedOrganizationId ? '/promote' : '/select-organization',
      });
    } catch (loginError) {
      const message =
        loginError && typeof loginError === 'object' && 'message' in loginError
          ? String(loginError.message)
          : 'Unable to start a session.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <AppCard className={styles.card}>
        <div className={styles.brand}>Promote</div>
        <h1 className={styles.title}>Sign in</h1>
        <p className={styles.subtitle}>Sign in to manage your ad campaigns.</p>

        {showEventsSSO && (
          <button type="button" onClick={handleEventsSSO} className={styles.eventsButton}>
            <img src={EVENTS_COM_ICON} alt="Events.com" width={20} height={20} />
            Sign in with Events.com
          </button>
        )}

        {showEventsSSO && showEmailLogin && (
          <div className={styles.divider}>
            <span>or</span>
          </div>
        )}

        {showEmailLogin && (
          <>
            <AppInput
              label="Organization Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="test@example.com"
              error={error ?? undefined}
            />

            <AppButton onClick={handleEmailLogin} loading={isLoading} disabled={!email}>
              Continue with Email
            </AppButton>
          </>
        )}
      </AppCard>
    </div>
  );
}
